import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth-provider";

const ADMIN_NAV = [
  { label: "Overview", path: "/admin", icon: "📊" },
  { label: "Tenants", path: "/admin/tenants", icon: "🏢" },
  { label: "Users", path: "/admin/users", icon: "👥" },
  { label: "Access Requests", path: "/admin/access-requests", icon: "🛂" },
  { label: "Billing", path: "/admin/billing", icon: "💳" },
  { label: "Tax Rates", path: "/admin/tax-rates", icon: "🧾" },
  { label: "Trial Funnel", path: "/admin/trial-funnel", icon: "📈" },
  { label: "Marketplace Review", path: "/admin/marketplace-review", icon: "🧩" },
  { label: "System Health", path: "/admin/health", icon: "🩺" },
  { label: "Feature Flags", path: "/admin/feature-flags", icon: "🚩" },
];

export function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="flex h-screen overflow-hidden bg-slate-950 text-slate-100">
      {/* Admin sidebar — dark theme */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
      <aside className={cn(
        "density-sidebar fixed inset-y-0 left-0 z-50 flex flex-col border-r border-[var(--color-border)] bg-[var(--color-background)] transition-transform duration-200 lg:static lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full",
      )}>
        <div className="border-b border-[var(--color-border)] p-4">
          <p className="text-sm font-bold text-white">Ease Platform</p>
          <p className="text-xs text-muted-foreground">Admin Dashboard</p>
        </div>
        <nav className="flex-1 overflow-y-auto p-3">
          <ul className="space-y-1">
            {ADMIN_NAV.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  end={item.path === "/admin"}
                  className={({ isActive }) => cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    isActive ? "bg-[var(--color-surface)] text-white" : "text-muted-foreground hover:bg-[var(--color-surface)]/50 hover:text-white",
                  )}
                >
                  <span>{item.icon}</span>
                  <span>{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className="border-t border-[var(--color-border)] p-3">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-[var(--color-surface)]/50 hover:text-white"
          >
            ← Back to dashboard
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-12 items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-background)] px-4">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="rounded-md p-1.5 text-muted-foreground hover:bg-[var(--color-surface)] lg:hidden">
            ☰
          </button>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{user?.name}</span>
            <button onClick={async () => { await signOut(); navigate("/login"); }}
              className="text-xs text-muted-foreground hover:text-white">Sign out</button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
